import React from 'react';
import { motion } from 'framer-motion';
import { Activity, MapPin, Zap, TrendingUp } from 'lucide-react';

interface StatsGridProps {
  stats: {
    totalDevices: number;
    activeDevices: number;
    totalReadings: number;
    recentReadings: number;
  };
  loading: boolean;
}

const StatsGrid: React.FC<StatsGridProps> = ({ stats, loading }) => {
  const statItems = [
    {
      id: 'total-devices',
      label: 'Total Devices',
      value: stats.totalDevices,
      icon: MapPin,
      color: 'bg-blue-500',
      trend: '+12%',
      trendUp: true,
    },
    {
      id: 'active-devices',
      label: 'Active Now',
      value: stats.activeDevices,
      icon: Activity,
      color: 'bg-green-500',
      trend: 'Real-time',
      trendUp: true,
    },
    {
      id: 'total-readings',
      label: 'Total Readings',
      value: stats.totalReadings,
      icon: TrendingUp,
      color: 'bg-traqpack-yellow',
      trend: '+24%',
      trendUp: true,
    },
    {
      id: 'recent-readings',
      label: 'Last 24h',
      value: stats.recentReadings,
      icon: Zap,
      color: 'bg-purple-500',
      trend: '+8%',
      trendUp: true,
    },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="stat-card animate-pulse">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-traqpack-gray-200 rounded-lg"></div>
              <div className="w-12 h-3 bg-traqpack-gray-200 rounded"></div>
            </div>
            <div className="w-16 h-5 bg-traqpack-gray-200 rounded mb-1"></div>
            <div className="w-20 h-3 bg-traqpack-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {statItems.map((item, index) => {
        const Icon = item.icon;
        
        return (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="stat-card group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 ${item.color} rounded-lg flex items-center justify-center text-white shadow-sm`}>
                <Icon className="w-4 h-4" />
              </div>
              
              <div className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                item.trendUp 
                  ? 'text-green-700 bg-green-100' 
                  : 'text-red-700 bg-red-100'
              }`}>
                {item.trend}
              </div>
            </div>
            
            <div className="mb-1">
              <div className="text-lg font-bold text-traqpack-gray-900 group-hover:text-traqpack-yellow transition-colors">
                {formatNumber(item.value)}
              </div>
            </div>
            
            <div className="text-xs font-medium text-traqpack-gray-600">
              {item.label}
            </div>
            
            {/* Subtle animation bar */}
            <div className="mt-2 h-0.5 bg-traqpack-gray-100 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${item.color} opacity-50`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, (item.value / Math.max(...statItems.map(s => s.value))) * 100)}%` }}
                transition={{ duration: 1, delay: index * 0.2 }}
              />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default StatsGrid;